/**
 * Content API endpoints: modules, lessons, content blocks, lesson progress,
 * course templates, and cloning.
 */

import { fetchApi } from '../core';
import type {
  CatalogCourseSummary,
  CloneCourseData,
  CreateLessonData,
  CreateModuleData,
  LessonProgressResponse,
  LessonResponse,
  MarkTemplateData,
  ModuleResponse,
  ReorderLessonsData,
  ReorderModulesData,
  UpdateLessonData,
  UpdateModuleData,
} from '../types';
import type { Course } from '@/types';

type RawObjectId = string | { $oid?: string } | null | undefined;

type RawCourse = Omit<Course, 'id' | 'source_course_id'> & {
  id?: string;
  _id?: RawObjectId;
  source_course_id?: RawObjectId;
};

function objectIdToString(value: RawObjectId): string | null {
  if (!value) return null;
  if (typeof value === 'string') return value;
  return typeof value.$oid === 'string' ? value.$oid : null;
}

function normalizeCourse(course: RawCourse): Course {
  return {
    ...course,
    id: course.id ?? objectIdToString(course._id) ?? '',
    source_course_id: objectIdToString(course.source_course_id),
  };
}

// ===== Modules =====

export function listModules(courseId: string, componentName: string = 'Unknown') {
  return fetchApi<ModuleResponse[]>(
    `/courses/${courseId}/modules`,
    {},
    componentName
  );
}

export function createModule(
  courseId: string,
  data: CreateModuleData,
  componentName: string = 'Unknown'
) {
  return fetchApi<ModuleResponse>(
    `/courses/${courseId}/modules`,
    { method: 'POST', body: JSON.stringify(data) },
    componentName
  );
}

export function updateModule(
  moduleId: string,
  data: UpdateModuleData,
  componentName: string = 'Unknown'
) {
  return fetchApi<ModuleResponse>(
    `/modules/${moduleId}`,
    { method: 'PUT', body: JSON.stringify(data) },
    componentName
  );
}

export function deleteModule(moduleId: string, componentName: string = 'Unknown') {
  return fetchApi<{ message: string }>(
    `/modules/${moduleId}`,
    { method: 'DELETE' },
    componentName
  );
}

export function reorderModules(
  courseId: string,
  data: ReorderModulesData,
  componentName: string = 'Unknown'
) {
  return fetchApi<ModuleResponse[]>(
    `/courses/${courseId}/modules/reorder`,
    { method: 'POST', body: JSON.stringify(data) },
    componentName
  );
}

// ===== Lessons =====

export function listLessons(moduleId: string, componentName: string = 'Unknown') {
  return fetchApi<LessonResponse[]>(
    `/modules/${moduleId}/lessons`,
    {},
    componentName
  );
}

export function createLesson(
  moduleId: string,
  data: CreateLessonData,
  componentName: string = 'Unknown'
) {
  return fetchApi<LessonResponse>(
    `/modules/${moduleId}/lessons`,
    { method: 'POST', body: JSON.stringify(data) },
    componentName
  );
}

export function getLesson(lessonId: string, componentName: string = 'Unknown') {
  return fetchApi<LessonResponse>(`/lessons/${lessonId}`, {}, componentName);
}

export function updateLesson(
  lessonId: string,
  data: UpdateLessonData,
  componentName: string = 'Unknown'
) {
  return fetchApi<LessonResponse>(
    `/lessons/${lessonId}`,
    { method: 'PUT', body: JSON.stringify(data) },
    componentName
  );
}

export function deleteLesson(lessonId: string, componentName: string = 'Unknown') {
  return fetchApi<{ message: string }>(
    `/lessons/${lessonId}`,
    { method: 'DELETE' },
    componentName
  );
}

export function reorderLessons(
  moduleId: string,
  data: ReorderLessonsData,
  componentName: string = 'Unknown'
) {
  return fetchApi<LessonResponse[]>(
    `/modules/${moduleId}/lessons/reorder`,
    { method: 'POST', body: JSON.stringify(data) },
    componentName
  );
}

export function completeLesson(lessonId: string, componentName: string = 'Unknown') {
  return fetchApi<LessonProgressResponse>(
    `/lessons/${lessonId}/complete`,
    { method: 'POST' },
    componentName
  );
}

// ===== Templates + cloning =====

export async function listTemplates(componentName: string = 'Unknown') {
  const courses = await fetchApi<RawCourse[]>(`/courses/templates`, {}, componentName);
  return courses.map(normalizeCourse);
}

export function listCatalog(componentName: string = 'Unknown') {
  return fetchApi<CatalogCourseSummary[]>(`/courses/catalog`, {}, componentName);
}

export async function markTemplate(
  courseId: string,
  data: MarkTemplateData,
  componentName: string = 'Unknown'
) {
  const course = await fetchApi<RawCourse>(
    `/courses/${courseId}/mark-template`,
    { method: 'POST', body: JSON.stringify(data) },
    componentName
  );
  return normalizeCourse(course);
}

export async function cloneCourse(
  courseId: string,
  data: CloneCourseData,
  componentName: string = 'Unknown'
) {
  const course = await fetchApi<RawCourse>(
    `/courses/${courseId}/clone`,
    { method: 'POST', body: JSON.stringify(data) },
    componentName
  );
  return normalizeCourse(course);
}
